import { useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite } from '../../ui/changes.ts';
import { Drawer } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { MasterList } from './types.ts';
import { MarketPicker, Tabs, slugify } from './ui.tsx';

export const GLOBAL_MERCHANTS_PATH = '/global-merchants';

/** Switches between merchants in a country and global merchants. */
export function MerchantTabs() {
  const { path } = useRouter();
  const global = path === GLOBAL_MERCHANTS_PATH;
  return (
    <Tabs>
      <Link to="/merchants" aria-current={!global ? 'page' : undefined}>
        In a country
      </Link>
      <Link to={GLOBAL_MERCHANTS_PATH} aria-current={global ? 'page' : undefined}>
        Global merchants
      </Link>
    </Tabs>
  );
}

interface SiteRef {
  ref: string;
  name: string;
}

/** Header button + drawer: add a merchant to a country (existing or new global merchant). */
export function AddMerchantButton({ market, sites }: { market: string; sites: SiteRef[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
        Add merchant
      </button>
      {open ? <AddMerchantDrawer market={market} sites={sites} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function AddMerchantDrawer({ market: initialMarket, sites, onClose }: { market: string; sites: SiteRef[]; onClose: () => void }) {
  const toast = useToast();
  const { navigate } = useRouter();
  const [market, setMarket] = useState(initialMarket);
  const [q, setQ] = useState('');
  const [master, setMaster] = useState<{ ref: string; name: string } | null>(null);
  const [form, setForm] = useState({ localName: '', slug: '', domain: '', shipping: '', returns: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [showOn, setShowOn] = useState<string[]>(sites.map((s) => s.ref));
  const found = useOp<MasterList>('admin.masterMerchants.list', { q: q || undefined, limit: 8 }, 0, q.trim().length >= 2 && !master);
  const name = master?.name ?? q.trim();
  const localName = form.localName || name;
  const slug = slugTouched ? form.slug : slugify(localName);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const input = {
      market,
      ...(master ? { masterRef: master.ref } : { newMaster: { name, ...(form.domain ? { domain: form.domain } : {}) } }),
      localName,
      slug,
      facts: { shipping: form.shipping, returns: form.returns },
      showOn: market === initialMarket ? showOn : [],
    };
    const result = await runWrite<{ ref: string }>(toast, 'admin.merchants.create', input, `${localName} added to ${market}`);
    if (result) navigate(`/merchants/${result.ref}`);
  };

  return (
    <Drawer title="Add merchant to a country" onClose={onClose}>
      <form onSubmit={submit} className="stack">
        <MarketPicker value={market} onChange={setMarket} />
        {master ? (
          <p>
            Global merchant: <strong>{master.name}</strong>{' '}
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setMaster(null)}>
              Change
            </button>
          </p>
        ) : (
          <label>
            Merchant name
            <input value={q} autoFocus required placeholder="Start typing: existing global merchants appear below" onChange={(e) => setQ(e.target.value)} />
          </label>
        )}
        {!master && q.trim().length >= 2 ? (
          <ul className="pick-list" aria-label="Existing global merchants">
            {(found.data?.rows ?? []).map((row) => (
              <li key={row.ref}>
                <button type="button" className="row-button" onClick={() => setMaster({ ref: row.ref, name: row.name })} disabled={row.markets.includes(market)}>
                  <span className="strong">{row.name}</span>
                  <small className="muted">{row.markets.includes(market) ? `already in ${market}` : `use existing · ${row.markets.join(', ') || 'no country yet'}`}</small>
                </button>
              </li>
            ))}
            <li className="muted small">Nothing fits? Submitting creates a new global merchant “{name}”.</li>
          </ul>
        ) : null}
        <div className="form-grid">
          <label>
            Name in {market || 'this country'}
            <input value={form.localName} placeholder={name} onChange={(e) => setForm({ ...form, localName: e.target.value })} />
          </label>
          <label>
            URL slug
            <input value={slug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} />
          </label>
          {!master ? (
            <label className="span-2">
              Main domain <small className="muted">adds a domain rule</small>
              <input value={form.domain} placeholder="example.co.uk" onChange={(e) => setForm({ ...form, domain: e.target.value })} />
            </label>
          ) : null}
          <label>
            Shipping
            <input value={form.shipping} onChange={(e) => setForm({ ...form, shipping: e.target.value })} />
          </label>
          <label>
            Returns
            <input value={form.returns} onChange={(e) => setForm({ ...form, returns: e.target.value })} />
          </label>
        </div>
        {market === initialMarket && sites.length ? (
          <fieldset>
            <legend>Show on</legend>
            <div className="radio-row">
              {sites.map((site) => (
                <label key={site.ref} className="inline-check">
                  <input type="checkbox" checked={showOn.includes(site.ref)} onChange={(e) => setShowOn(e.target.checked ? [...showOn, site.ref] : showOn.filter((r) => r !== site.ref))} />
                  {site.name}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={!name || !slug}>
            Add merchant
          </button>
        </div>
      </form>
    </Drawer>
  );
}

/** Appears when merchants are selected in the list: show or hide them on sites in one go. */
export function MerchantBulkBar({ selected, sites, onDone }: { selected: string[]; sites: SiteRef[]; onDone: () => void }) {
  const toast = useToast();
  const [targets, setTargets] = useState<string[]>(sites.map((s) => s.ref));
  if (selected.length === 0) return null;
  const apply = async (shown: boolean) => {
    const result = await runWrite<{ added: number; updated: number; unchanged: number }>(
      toast,
      'admin.merchants.addToSites',
      { merchantRefs: selected, propertyRefs: targets, shown },
      (r) => `${shown ? 'Shown' : 'Hidden'}: ${r.added + r.updated} changed, ${r.unchanged} already so`,
    );
    if (result) onDone();
  };
  return (
    <div className="bulk-bar" role="region" aria-label="Selected merchants">
      <strong>{selected.length} selected</strong>
      <span className="muted">on</span>
      {sites.map((site) => (
        <label key={site.ref} className="inline-check">
          <input type="checkbox" checked={targets.includes(site.ref)} onChange={(e) => setTargets(e.target.checked ? [...targets, site.ref] : targets.filter((r) => r !== site.ref))} />
          {site.name}
        </label>
      ))}
      <button type="button" className="btn btn-small btn-primary" disabled={!targets.length} onClick={() => apply(true)}>
        Show on sites
      </button>
      <button type="button" className="btn btn-small" disabled={!targets.length} onClick={() => apply(false)}>
        Hide
      </button>
      <button type="button" className="btn btn-small btn-ghost" onClick={onDone}>
        Clear
      </button>
    </div>
  );
}
